import { useEffect, useRef } from 'react'

import type { ConsentCategory } from '../core/constants'
import { useConsentPreferences } from './collect'

/**
 * Returns `true` if and only if the user has consented to the given category.
 * @param consentCategory the name of the consent category
 * @returns
 */
export function useHasConsent(consentCategory: ConsentCategory) {
  const consents = useConsentPreferences()
  return consents.allowed.has(consentCategory)
}

type ConsentRevokedCallback = () => void
type ConsentGrantedCallback = () => void | ConsentRevokedCallback

/**
 * Like {@link useEffect}, but applied when consent is granted/revoked for a given
 * consent category.
 *
 * @example
 * ```ts
 * useConsentEffect('advertising', () => {
 *   const Some3rdPartySdk = Tracker.init()
 *   console.log('3rd-party SDK initialised')
 *   return () => {
 *     Some3rdPartySdk.destroy()
 *     console.log('3rd-party SDK destroyed')
 *   }
 * })
 * ```
 *
 * @param consentCategory
 *   The name of the consent category to apply the effect to

 * @param onConsentGranted
 *   The callback to execute when consent is granted.
 *
 *   If this returns a callback, it will be executed when consent is revoked
 *   (or when the component unmounts).
 */
export function useConsentEffect(
  consentCategory: ConsentCategory,
  onConsentGranted: ConsentGrantedCallback,
) {
  const hasConsent = useHasConsent(consentCategory)
  const onConsentRevoked = useRef<ConsentRevokedCallback | void>()

  useEffect(() => {
    // Calls `onConsentGranted` if not previously granted
    function handleConsentGranted() {
      onConsentRevoked.current ??= onConsentGranted()
    }

    // Calls `onConsentRevoked` if previously granted, but not yet revoked
    function handleConsentRevoked() {
      onConsentRevoked.current?.()
      onConsentRevoked.current = undefined
    }

    if (hasConsent) {
      handleConsentGranted()
    } else {
      handleConsentRevoked()
    }

    return handleConsentRevoked
  }, [hasConsent, onConsentGranted])
}

type MaybeAsync<T> = T | Promise<T>

/** Like {@link useConsentEffect}, but accepting asynchronous callbacks. */
export function useAsyncConsentEffect(
  consentCategory: ConsentCategory,
  onConsentGranted: () => MaybeAsync<void | (() => MaybeAsync<void>)>,
) {
  useConsentEffect(consentCategory, () => {
    let cancelled = false
    let cleanup: void | (() => MaybeAsync<void>)

    async function run() {
      const onConsentRevoked = await onConsentGranted()
      if (!cancelled) {
        cleanup = onConsentRevoked
      }
    }
    run()

    return () => {
      cancelled = true
      cleanup?.()
    }
  })
}
